import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { togglePinnedFiltersState, selectBookingsPinnedFilters } from '@store';

import {
  contractPeriodOptions,
  contractTypeOptions,
  originOptions,
  stages,
} from 'components/bookings/constants';
import { useHasFilters } from 'hooks/common';

const defaultOption = {
  text: '-',
  value: undefined,
};

export const useBookingsFilterConfig = ({
  customerTypesOptions,
  bookingLabelsOptions,
  assignees,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const pinnedFilters = useSelector(selectBookingsPinnedFilters);

  const filterCategories = useMemo(
    () => [
      {
        id: 'origin',
        label: 'Origin',
        icon: RadioButtonUncheckedIcon,
        options: [defaultOption, ...originOptions],
        multiSelect: false,
      },
      {
        id: 'contractType',
        label: 'Contract Type',
        icon: RadioButtonUncheckedIcon,
        options: [defaultOption, ...contractTypeOptions],
        multiSelect: false,
      },
      {
        id: 'contractPeriod',
        label: 'Contract Period',
        icon: RadioButtonUncheckedIcon,
        options: [defaultOption, ...contractPeriodOptions],
        multiSelect: false,
      },
      {
        id: 'customerTypes',
        label: 'Customer Types',
        icon: RadioButtonUncheckedIcon,
        options: customerTypesOptions || [],
        multiSelect: true,
      },
      {
        id: 'stage',
        label: 'Stage',
        icon: RadioButtonUncheckedIcon,
        options: [defaultOption, ...stages],
        multiSelect: false,
      },
      {
        id: 'assignee',
        label: 'Assignee',
        icon: RadioButtonUncheckedIcon,
        options: assignees || [],
        multiSelect: false,
      },
      {
        id: 'labels',
        label: 'Labels',
        icon: RadioButtonUncheckedIcon,
        options: bookingLabelsOptions || [],
        multiSelect: true,
      },
    ],
    [assignees, customerTypesOptions, bookingLabelsOptions]
  );

  const handlePinnedFilterSelect = (pinnedFilter) => {
    dispatch(togglePinnedFiltersState({ board: 'bookings', pinnedFilter }));
  };

  const { hasFilters } = useHasFilters({
    pinnedFilters,
    omitParams: ['page', 'viewMode', 'pageSize', 'order', 'orderBy', 'query'],
  });

  const currentFilters = useMemo(() => {
    const parseQueryValue = (value, delimiter = ',') => {
      if (!value) return [];
      if (typeof value === 'string') {
        return value.split(delimiter).filter(Boolean);
      }
      return Array.isArray(value) ? value : [];
    };

    return {
      origin: parseQueryValue(router.query.origin),
      contractType: parseQueryValue(router.query.contractType),
      contractPeriod: parseQueryValue(router.query.contractPeriod),
      customerTypes: parseQueryValue(router.query.customerTypes),
      stage: parseQueryValue(router.query.stage),
      assignee: parseQueryValue(router.query.assignee),
      labels: parseQueryValue(router.query.labels),
    };
  }, [router.query]);

  return {
    filterCategories,
    currentFilters,
    pinnedFilters,
    supportPinnedFilters: true,
    hasFilters,
    handlePinnedFilterSelect,
  };
};
